import React, { useState, useEffect } from 'react';
import { UserCheck, UserX, Users, Banknote, ChevronDown, ChevronUp, Info, User, AlertCircle, Loader, Mail } from 'lucide-react';
import { useAuth } from '../../../App';
import { useTranslation } from '../../../contexts/LanguageContext';

const InvitationsList = ({ invitations = [], onInvitationResponse, isLoading }) => {
  const { t } = useTranslation();
  const { httpClient } = useAuth(); // Get httpClient from auth context
  
  const [expandedInvitations, setExpandedInvitations] = useState({});
  const [logosLoaded, setLogosLoaded] = useState({});
  const [spaceDetails, setSpaceDetails] = useState({});
  const [loadingSpaces, setLoadingSpaces] = useState({});

  useEffect(() => {
    const fetchSpaceDetails = async () => {
      const newSpaceDetails = { ...spaceDetails };
      const newLoadingSpaces = { ...loadingSpaces };
      
      for (const invite of invitations) {
        const spaceId = invite.sharing_space_id || invite.spaceId;
        
        if (spaceId && !spaceDetails[spaceId] && !loadingSpaces[spaceId]) {
          newLoadingSpaces[spaceId] = true;
          setLoadingSpaces(newLoadingSpaces);
          
          try {
            const response = await httpClient.request(`/api/sharing-space/${spaceId}`);
            
            if (response.ok) {
              const spaceData = await response.json();
              newSpaceDetails[spaceId] = spaceData;
            } else {
              console.error(`Failed to fetch space details for ${spaceId}`);
              newSpaceDetails[spaceId] = null;
            }
          } catch (error) {
            console.error(`Error fetching space details for ${spaceId}:`, error);
            newSpaceDetails[spaceId] = null;
          } finally {
            delete newLoadingSpaces[spaceId];
          }
        }
      }
      
      setSpaceDetails(newSpaceDetails);
      setLoadingSpaces(newLoadingSpaces);
    };
    
    if (invitations.length > 0 && httpClient) {
      fetchSpaceDetails();
    }
  }, [invitations, httpClient]);

  const toggleExpansion = (invitationId, spaceId) => {
    setExpandedInvitations(prev => ({
      ...prev,
      [invitationId]: !prev[invitationId]
    }));
    
    if (spaceId && !spaceDetails[spaceId] && !loadingSpaces[spaceId] && httpClient) {
      fetchSpaceDetailsForId(spaceId);
    }
  };

  const fetchSpaceDetailsForId = async (spaceId) => {
    if (!spaceId || spaceDetails[spaceId] || loadingSpaces[spaceId] || !httpClient) return;
    
    setLoadingSpaces(prev => ({ ...prev, [spaceId]: true }));
    
    try {
      const response = await httpClient.request(`/api/sharing-space/${spaceId}`);
      
      if (response.ok) {
        const spaceData = await response.json();
        setSpaceDetails(prev => ({ ...prev, [spaceId]: spaceData }));
      } else {
        console.error(`Failed to fetch space details for ${spaceId}`);
        setSpaceDetails(prev => ({ ...prev, [spaceId]: null }));
      }
    } catch (error) {
      console.error(`Error fetching space details for ${spaceId}:`, error);
      setSpaceDetails(prev => ({ ...prev, [spaceId]: null }));
    } finally {
      setLoadingSpaces(prev => {
        const newState = { ...prev };
        delete newState[spaceId];
        return newState;
      });
    }
  };

  const handleImageLoad = (invitationId) => {
    setLogosLoaded(prev => ({
      ...prev,
      [invitationId]: true
    }));
  };

  const calculateCosts = (space) => {
    if (!space || !space.plan_list || space.plan_list.length === 0) {
      return { monthly: 0, perPerson: 0 };
    }
    
    let monthlyCost = 0;
    
    space.plan_list.forEach(plan => {
      const price = parseFloat(plan.price || 0);
      
      if (plan.billing_interval === 'monthly') {
        monthlyCost += price;
      } else if (plan.billing_interval === 'yearly') {
        monthlyCost += price / 12;
      } else {
        monthlyCost += price;
      }
    });
    
    const acceptedMembers = space.members ? 
      space.members.filter(m => 
        m.request_status === 'accepted' || m.request_status === undefined
      ).length : 1;
    
    const totalMembersAfterJoin = Math.max(acceptedMembers, 1) + 1;
    
    return {
      monthly: monthlyCost,
      perPerson: totalMembersAfterJoin > 0 ? (monthlyCost / totalMembersAfterJoin) : monthlyCost
    };
  };

  if (invitations.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <Mail className="h-10 w-10 text-gray-400" />
        </div>
        <h3 className="text-xl font-medium text-gray-600">
          {t('sharesubs.invitationsList.noInvitations')}
        </h3>
        <p className="text-gray-500">
          {t('sharesubs.invitationsList.noInvitationsDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {invitations.map(invite => {
        const spaceId = invite.sharing_space_id || invite.spaceId;
        const space = spaceId ? spaceDetails[spaceId] : null;
        const isExpanded = expandedInvitations[invite._id];
        const isSpaceLoading = spaceId && loadingSpaces[spaceId];
        
        const costs = space ? calculateCosts(space) : { monthly: 0, perPerson: 0 };
        
        const acceptedMembers = space && space.members ? 
          space.members.filter(m => 
            m.request_status === 'accepted' || m.request_status === undefined
          ) : 
          [];
        
        const memberCount = space ? Math.max(acceptedMembers.length, 1) : 0;
        
        const adminInfo = space && space.admin_username ? 
          `${space.admin_username}${space.admin_email ? ` (${space.admin_email})` : ''}` : 
          invite.adminName || t('sharesubs.invitationsList.spaceAdmin');

        return (
          <div key={invite._id} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <div className="p-4 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg text-gray-800">
                    {space ? space.name : invite.spaceName || t('sharesubs.invitationsList.newSharingSpace')}
                  </h3>
                  <div className="flex items-center mt-1 text-sm text-gray-600">
                    <User className="h-4 w-4 mr-1" />
                    <span>{t('sharesubs.invitationsList.invitedBy')} {adminInfo}</span>
                  </div>
                  {space && space.description && (
                    <p className="text-sm text-gray-500 mt-2">{space.description}</p>
                  )}
                </div>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => onInvitationResponse(invite._id, spaceId, 'accept')}
                    className="px-4 py-2 bg-[#253985] text-white rounded-lg flex items-center font-bold hover:bg-[#1a2c6d] transition-colors"
                    disabled={isLoading}
                  >
                    <UserCheck className="h-4 w-4 mr-1" />
                    {t('sharesubs.invitationsList.accept')}
                  </button>
                  <button
                    onClick={() => onInvitationResponse(invite._id, spaceId, 'reject')}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg flex items-center font-bold hover:bg-red-700 transition-colors"
                    disabled={isLoading}
                  >
                    <UserX className="h-4 w-4 mr-1" />
                    {t('sharesubs.invitationsList.reject')}
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  {space ? (
                    <>
                      <div className="flex items-center bg-blue-50 px-2 py-1 rounded-md">
                        <Users className="h-4 w-4 mr-1 text-[#253985]" />
                        <span className="font-medium">
                          {memberCount} {memberCount === 1 ? t('sharesubs.members.member') : t('sharesubs.members.member_plural')}
                        </span>
                      </div>
                      
                      <div className="flex items-center bg-blue-50 px-2 py-1 rounded-md">
                        <Banknote className="h-4 w-4 mr-1 text-[#253985]" />
                        <span className="font-medium">
                          €{costs.perPerson.toFixed(2)} {t('sharesubs.invitationsList.costIfJoin')}
                        </span>
                      </div>
                    </>
                  ) : isSpaceLoading ? (
                    <div className="flex items-center">
                      <Loader className="h-4 w-4 mr-1 animate-spin text-[#253985]" />
                      <span>{t('sharesubs.invitationsList.loadingSpaceDetails')}...</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1 text-yellow-500" />
                      <span>{t('sharesubs.invitationsList.spaceDetailsUnavailable')}</span>
                    </div>
                  )}
                </div>
                
                {space && (
                  <button
                    onClick={() => toggleExpansion(invite._id, spaceId)}
                    className="text-[#253985] hover:text-[#1a2c6d] flex items-center text-sm font-medium"
                  >
                    {isExpanded ? (
                      <>
                        <span className="mr-1">{t('sharesubs.invitationsList.lessDetails')}</span>
                        <ChevronUp className="h-4 w-4" />
                      </>
                    ) : (
                      <>
                        <span className="mr-1">{t('sharesubs.invitationsList.moreDetails')}</span>
                        <ChevronDown className="h-4 w-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {isExpanded && space && (
              <div className="bg-gray-50 p-4 border-t">
                {space.plan_list && space.plan_list.length > 0 ? (
                  <div className="mb-4">
                    <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                      <Info className="h-5 w-5 mr-2 text-[#253985]" />
                      {t('sharesubs.invitationsList.sharedSubscriptions')} ({space.plan_list.length})
                    </h4>
                    <div className="flex -space-x-2">
                      {space.plan_list.map((plan, index) => (
                        <div 
                          key={index} 
                          className="relative w-10 h-10 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center overflow-hidden"
                          title={plan.plan_name}
                        >
                          {plan.logo ? (
                            <img 
                              src={plan.logo} 
                              alt={plan.plan_name}
                              className="w-full h-full object-cover"
                              onLoad={() => handleImageLoad(`${invite._id}-${index}`)}
                              style={{ 
                                display: logosLoaded[`${invite._id}-${index}`] ? 'block' : 'none' 
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-blue-100">
                              <span className="text-xs font-bold text-[#253985]">
                                {plan.plan_name ? plan.plan_name.substring(0, 2).toUpperCase() : 'SU'}
                              </span>
                            </div>
                          )}
                          {!logosLoaded[`${invite._id}-${index}`] && plan.logo && (
                            <div className="absolute inset-0 flex items-center justify-center bg-blue-100">
                              <span className="text-xs font-bold text-[#253985]">
                                {plan.plan_name ? plan.plan_name.substring(0, 2).toUpperCase() : 'SU'}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 text-sm text-gray-500">
                    {t('sharesubs.invitationsList.noSubscriptions')}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3 text-sm mb-4 bg-white p-3 rounded-lg border border-gray-200">
                  <div className="text-gray-600 font-medium">
                    {t('sharesubs.invitationsList.totalMonthlyCost')}:
                  </div>
                  <div className="font-bold text-right text-[#253985]">€{costs.monthly.toFixed(2)}</div>
                  
                  <div className="text-gray-600 font-medium">
                    {t('sharesubs.invitationsList.yourShare')}:
                  </div>
                  <div className="font-bold text-right text-[#253985]">€{costs.perPerson.toFixed(2)}/mo</div>
                  
                  <div className="text-gray-600 font-medium">
                    {t('sharesubs.invitationsList.billingCycle')}:
                  </div>
                  <div className="font-medium text-right">
                    {space.plan_list && space.plan_list.some(p => p.billing_interval === 'yearly') ? 
                      t('sharesubs.invitationsList.mixedBilling') : t('sharesubs.invitationsList.monthlyBilling')}
                  </div>
                </div>
                
                {acceptedMembers.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                      <Users className="h-5 w-5 mr-2 text-[#253985]" />
                      {t('sharesubs.invitationsList.currentMembers')}
                    </h4>
                    <div className="space-y-2">
                      {acceptedMembers.map((member, index) => (
                        <div 
                          key={index} 
                          className="flex items-center text-sm bg-white p-2 rounded-lg border border-gray-200"
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <User className="h-4 w-4 text-[#253985]" />
                          </div>
                          <span className="truncate font-medium">
                            {member.username || member.member_email}
                            {member.member_id === space.admin_id && (
                              <span className="ml-2 text-xs bg-[#253985] text-white px-2 py-0.5 rounded">
                                {t('sharesubs.members.admin')}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default InvitationsList;